import { Routes } from '@angular/router';
import { RoleGuard } from '../../core/guard/role.guard';
import { PackingBoxViewAuthService } from '../../views/packing-box/services/auth.service';
import { PackingBoxViewService } from '../../views/packing-box/services/view.service';
import { WebSocketApiLabelService } from '../../api/elite/websockets/label.service';
import { PackingBoxViewApiService } from '../../views/packing-box/api/view.service';
import { TableService } from 'src/app/core/components/table/table.service';
import { PackingBoxZebraTableService } from 'src/app/views/packing-box/components/table/table-zebra.service';
import { PackingBoxViewZebraApiService } from 'src/app/views/packing-box/api/view-zebra.service';
import { SingleDepartmentService } from '../../shared/services/single-department.service';
import {
  FilterDepartmentService,
  FiltersPackingBoxAuthService,
} from './services';
import { PACKING_BOX_SPECIFY_STATION } from '../../views/packing-box/tokens/printer-station';
import { SchedulerItemOnMachineApiAbstract } from '../../common/scheduler/api/item-on-machine';
import { SchedulerAuthService } from '../../common/scheduler/services/auth.service';
import {
  FilterSchedulerAuthService,
  FilterSchedulerItemOnMachineService,
} from '@dep/filters/view-scheduler/services';
import { SchedulerDragDropApiAbstract } from '../../common/scheduler/api/drag-drop';
import { SchedulerWorkOrderOnMachineApiService } from '../../common/scheduler/api/work-order-on-machine.service';
import { SchedulerMachineDragDropService } from '../../common/scheduler/services/machine-drag-drop.service';
import { FilterSchedulerDragDropMachineService } from '@dep/filters/view-scheduler/components/machine/drag-drop-machine.service';
import { SchedulerItemApiAbstract } from '../../common/scheduler/api/item-api';
import { SchedulerWorkOrderApiService } from '../../common/scheduler/api/work-order.service';
import { FilterSchedulerWorkOrderService } from '@dep/filters/view-scheduler/api/work-order.service';
import { QualityControlAuthService } from '../../common/quality-control/services/auth.service';
import { FiltersAuthQcService } from '@dep/filters/view-quality-control/services/auth.service';
import { FiltersQcViewTableApiService } from '@dep/filters/view-quality-control/api/table.service';
import { QualityControlViewTableApiService } from '../../views/quality-control/api/table.service';
import { FiltersQualityControlViewService } from '@dep/filters/view-quality-control/services/view.service';
import { QualityControlViewService } from '../../views/quality-control/services/view.service';
import { QC_INSPECTION_STAGE_TOKEN } from '../../common/quality-control/tokens/token';
import { FiltersQualityControlViewApiService } from '@dep/filters/view-quality-control/api/view.service';
import { SchedulerContainerService } from '../../common/scheduler/services/container.service';
import { SchedulerListDragDropService } from '../../common/scheduler/services/list-drag-drop.service';
import { FILTERS_ROLES_VIEWS_PERMISSIONS } from '@dep/filters/utils';
import { FilterValidationAuthService } from './view-validation/services/auth-validation.service';
import { WorkOrderValidationAuthService } from '../../views/wo-validation/services/auth.service';

const qualityControlView = (path: string, roles: string[]): Routes[0] => ({
  path,
  loadComponent: () =>
    import('../../views/quality-control/quality-control.component').then(
      (m) => m.QualityControlComponent
    ),
  canActivate: [RoleGuard],
  data: {
    breadcrumb: 'Quality Control',
    department: 'Hoses',
    optionsTab: schedulerOptionsTab,
    expectedRole: roles,
    stage: 'Quality Control',
  },
  providers: [
    {
      provide: QualityControlViewService,
      useClass: FiltersQualityControlViewService,
    },
    { provide: QualityControlAuthService, useClass: FiltersAuthQcService },
    {
      provide: QualityControlViewTableApiService,
      useClass: FiltersQcViewTableApiService,
    },
    { provide: QC_INSPECTION_STAGE_TOKEN, useFactory: () => 'Quality Control' },
    {
      provide: SchedulerItemApiAbstract,
      useClass: SchedulerWorkOrderApiService,
    },
    {
      provide: SchedulerDragDropApiAbstract,
      useClass: SchedulerWorkOrderOnMachineApiService,
    },
    FiltersQualityControlViewApiService,
    SchedulerListDragDropService,
    SchedulerContainerService,
  ],
});

export const FilterDepartmentAuthRoutes = FILTERS_ROLES_VIEWS_PERMISSIONS;

const schedulerOptionsTab = [
  {
    name: 'Production',
    route: './production',
    pathDestination: 'production',
  },
  {
    name: 'Packing',
    route: './packing',
    pathDestination: 'packing',
  },
];

const routes: Routes = [
  {
    path: 'scheduling',
    loadComponent: () =>
      import(
        '../../shared/components/routing-tabs/routing-tabs.component'
      ).then((m) => m.RoutingTabsComponent),
    canActivate: [RoleGuard],
    data: {
      pageTitle: 'Production',
      breadcrumb: 'Filters',
      expectedRole:
        FilterDepartmentAuthRoutes.sharedActions.schedulingTypeIdProduction,
      optionsTab: schedulerOptionsTab,
    },
    providers: [
      {
        provide: SchedulerItemOnMachineApiAbstract,
        useExisting: FilterSchedulerItemOnMachineService,
      },
      {
        // Reuse the same instance as `SchedulerItemOnMachineApiAbstract` so
        // it shares the TYPE token (`work_order` vs `sub_work_order`) that
        // the scheduler component sets in ngOnInit. Otherwise drag-drop
        // operations (removeItem, updateRank) always hit the work-order
        // endpoint, breaking the packing view that works with sub-work-orders.
        provide: SchedulerDragDropApiAbstract,
        useExisting: FilterSchedulerItemOnMachineService,
      },
      {
        provide: SchedulerMachineDragDropService,
        useClass: FilterSchedulerDragDropMachineService,
      },
      {
        provide: SchedulerItemApiAbstract, // ok
        useClass: FilterSchedulerWorkOrderService,
      },
    ],
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'production' },
      {
        path: 'production',
        loadComponent: () =>
          import('./view-scheduler/scheduler.component').then(
            (m) => m.FilterSchedulerComponent
          ),
        data: {
          pageTitle: 'Production',
          optionsTab: schedulerOptionsTab,
          stage: 'Machines Section',
          expectedRole:
            FilterDepartmentAuthRoutes.sharedActions.schedulingTypeIdProduction,
        },
      },
      {
        path: 'packing',
        loadComponent: () =>
          import('./view-scheduler/scheduler.component').then(
            (m) => m.FilterSchedulerComponent
          ),
        data: {
          pageTitle: 'Packing',
          optionsTab: schedulerOptionsTab,
          stage: 'Filter Packing',
          expectedRole:
            FilterDepartmentAuthRoutes.sharedActions.schedulingTypeIdPacking,
        },
      },
    ],
  },
  qualityControlView(
    'quality-control',
    FilterDepartmentAuthRoutes.sharedActions.addInspectionSheets
  ),

  {
    path: 'validation',
    loadComponent: () =>
      import('../../views/wo-validation/wo-validation.component').then(
        (m) => m.WorkOrderValidationComponent
      ),
    canActivate: [RoleGuard],
    data: {
      pageTitle: 'Production Report',
      breadcrumb: '',
      expectedRole: FilterDepartmentAuthRoutes.sharedActions.validationTypeId,
    },
    providers: [
      {
        provide: WorkOrderValidationAuthService,
        useClass: FilterValidationAuthService,
      },
    ],
  },
  {
    path: 'packing-box',
    loadComponent: () =>
      import('../../views/packing-box/packing-box.component').then(
        (m) => m.PackingBoxComponent
      ),
    canActivate: [RoleGuard],
    providers: [
      {
        provide: PackingBoxViewAuthService,
        useClass: FiltersPackingBoxAuthService,
      },
      { provide: TableService, useClass: PackingBoxZebraTableService },
      PackingBoxViewService,
      {
        provide: PackingBoxViewApiService,
        useClass: PackingBoxViewZebraApiService,
      },
      WebSocketApiLabelService,
    ],
    data: {
      breadcrumb: 'Packing Box',
      department: 'Filter',
      pageTitle: 'Crear etiquetas',
      companyType: 'sb',
      expectedRole: FilterDepartmentAuthRoutes.sharedActions.packingBox,
    },
  },

  {
    path: 'qc-data',
    loadComponent: () =>
      import(
        '../../views/quality-control-inspection-data/quality-control-inspection-data.component'
      ).then((m) => m.QualityControlInspectionDataComponent),
    canActivate: [RoleGuard],
    data: {
      breadcrumb: 'Form',
      pageTitle: 'Añadir datos de QC',
      department: 'Filter',
      expectedRole:
        FilterDepartmentAuthRoutes.sharedActions.addInspectionSheets,
    },
  },

  { path: '**', redirectTo: 'scheduling/:typeId', pathMatch: 'full' },
  { path: '', pathMatch: 'full', redirectTo: 'scheduling/:typeId' },
];

export const FilterDepartmentRoutes = {
  path: 'filters-dep',
  children: routes,
  providers: [
    { provide: SingleDepartmentService, useClass: FilterDepartmentService },
    {
      provide: PACKING_BOX_SPECIFY_STATION,
      useValue: true,
    },
    {
      provide: SchedulerAuthService,
      useClass: FilterSchedulerAuthService,
    },
  ],
};
